/**
 * Login Page
 *
 * Provides user authentication with email/password form.
 * Redirects to original requested page after successful login.
 * Handles unverified email case with resend option.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Alert,
  Anchor,
  Stack,
  Center,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconMail, IconCheck } from '@tabler/icons-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { LoginCredentials } from '@/types/auth';

const RESEND_COOLDOWN_SECONDS = 60;

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Extract redirect location from state (fallback to '/')
  const from = (location.state as { from?: string })?.from || '/';

  // Cooldown timer
  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setTimeout(() => {
        setCooldownRemaining(cooldownRemaining - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownRemaining]);

  const form = useForm<LoginCredentials>({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : 'Invalid email address',
      password: (value: string) =>
        value.length > 0 ? null : 'Password is required',
    },
  });

  const handleResendVerification = async () => {
    if (!unverifiedEmail || cooldownRemaining > 0) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: unverifiedEmail,
      });

      if (!resendError) {
        setResendSuccess(true);
        setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
      }
    } catch {
      // Silently fail - user can try again
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (values: LoginCredentials) => {
    setError(null);
    setEmailNotConfirmed(false);
    setResendSuccess(false);
    setIsLoading(true);

    try {
      await login(values);
      // Login successful, redirect to original location
      navigate(from, { replace: true });
    } catch (err) {
      // Display error message
      const errorMessage = err instanceof Error ? err.message : 'Login failed';

      // Check if it's an email not confirmed error
      if (errorMessage.toLowerCase().includes('email not confirmed')) {
        setEmailNotConfirmed(true);
        setUnverifiedEmail(values.email);
        setError('Please verify your email address before signing in.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
      <Center>
        <Stack gap="md" w="100%">
          <Title ta="center">Welcome back</Title>
          <Text c="dimmed" size="sm" ta="center">
            Sign in to your ReefDataHub account
          </Text>

          <Paper withBorder p={30} mt={30} radius="md">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                {error && (
                  <Alert
                    icon={emailNotConfirmed ? <IconMail size={16} /> : <IconAlertCircle size={16} />}
                    title={emailNotConfirmed ? 'Email verification required' : 'Error'}
                    color={emailNotConfirmed ? 'yellow' : 'red'}
                  >
                    <Stack gap="xs">
                      <Text size="sm">{error}</Text>
                      {emailNotConfirmed && (
                        <>
                          {resendSuccess ? (
                            <Alert icon={<IconCheck size={14} />} color="green" p="xs">
                              Verification email sent! Check your inbox.
                            </Alert>
                          ) : (
                            <Button
                              size="xs"
                              variant="light"
                              onClick={handleResendVerification}
                              loading={isResending}
                              disabled={cooldownRemaining > 0}
                            >
                              {cooldownRemaining > 0
                                ? `Resend in ${cooldownRemaining}s`
                                : 'Resend verification email'}
                            </Button>
                          )}
                        </>
                      )}
                    </Stack>
                  </Alert>
                )}

                <TextInput
                  label="Email"
                  placeholder="you@example.com"
                  required
                  {...form.getInputProps('email')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  required
                  {...form.getInputProps('password')}
                />

                <Button type="submit" fullWidth loading={isLoading}>
                  Sign in
                </Button>

                <Text size="sm" ta="center">
                  <Anchor component={Link} to="/forgot-password" size="sm">
                    Forgot password?
                  </Anchor>
                </Text>

                <Text size="sm" ta="center" c="dimmed">
                  Don't have an account?{' '}
                  <Anchor component={Link} to="/register" size="sm">
                    Join the waitlist
                  </Anchor>
                </Text>
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Center>
    </Container>
  );
};
